/**
 * Generates a cryptographically random string for PKCE
 */
function generateRandomString(length: number): string {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], '');
}

/**
 * Generates a code verifier for PKCE
 */
export function generateCodeVerifier(): string {
    return generateRandomString(128);
}

/**
 * Generates a code challenge from a code verifier using SHA256
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

/**
 * Generates PKCE parameters and stores the verifier in sessionStorage
 * Returns the code challenge and method
 */
export async function generatePKCEParams(): Promise<{ codeChallenge: string; codeVerifier: string }> {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Store verifier in sessionStorage for later use during token exchange
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    
    return { codeChallenge, codeVerifier };
}

/**
 * Retrieves the stored code verifier from sessionStorage
 */
export function getStoredCodeVerifier(): string | null {
    return sessionStorage.getItem('pkce_code_verifier');
}

/**
 * Clears the stored code verifier from sessionStorage
 */
export function clearStoredCodeVerifier(): void {
    sessionStorage.removeItem('pkce_code_verifier');
}

/**
 * Stores the path to return to once the OAuth callback completes
 */
export function storeReturnPath(path: string): void {
    sessionStorage.setItem('pkce_return_path', path);
}

/**
 * Retrieves the stored post-login return path
 */
export function getStoredReturnPath(): string | null {
    return sessionStorage.getItem('pkce_return_path');
}

/**
 * Clears the stored post-login return path
 */
export function clearStoredReturnPath(): void {
    sessionStorage.removeItem('pkce_return_path');
}

